import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabase';
import { localToday } from '../utils/dateUtils';
import { emitTareaChanged } from '../utils/tareasBus';
import { onDataChanged } from '../utils/dataBus';
import TareaModal from '../components/TareaModal';
const formatDate = (d) => d ? new Date(d + 'T12:00:00').toLocaleDateString('es-AR') : '—';
const PRIORIDAD_PESO = { urgente: 0, 'muy importante': 1, normal: 2, baja: 3 };
const prioridadColor = (p) => ({ urgente: 'var(--red)', 'muy importante': '#f59e0b', normal: 'var(--blue)', baja: 'var(--text4)' }[p] || 'var(--blue)');

const diasDesdeCreacion = (createdAt) => {
  if (!createdAt) return null;
  const dias = Math.floor((Date.now() - new Date(createdAt).getTime()) / 86400000);
  if (dias === 0) return 'hoy';
  if (dias === 1) return 'hace 1 día';
  return `hace ${dias} días`;
};

function GrupoFecha({ titulo, subtitulo, count, children, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={{ border: '1px solid var(--border2)', borderRadius: 10, overflow: 'hidden' }}>
      <div onClick={() => setOpen(v => !v)}
        style={{ padding: '10px 16px', background: 'var(--surface2)', display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', userSelect: 'none', borderBottom: open ? '1px solid var(--border2)' : 'none' }}>
        <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text)', textTransform: 'uppercase', letterSpacing: 0.5 }}>{titulo}</span>
        {subtitulo && <span style={{ fontSize: 11, color: 'var(--text4)' }}>{subtitulo}</span>}
        {count != null && <span style={{ fontSize: 11, color: 'var(--text4)', background: 'var(--surface)', border: '1px solid var(--border2)', borderRadius: 10, padding: '0 7px' }}>{count}</span>}
        <span style={{ fontSize: 11, color: 'var(--text4)', marginLeft: 'auto' }}>{open ? '▼' : '▶'}</span>
      </div>
      {open && <div>{children}</div>}
    </div>
  );
}

function GrupoWidget({ g, renderTarea }) {
  const [open, setOpen] = useState(true);
  const color = 'var(--text2)';

  // Sub-agrupar por campo `grupo` de cada tarea
  const subGrupos = {};
  const sinSubGrupo = [];
  g.tareas.forEach(t => {
    if (t.grupo) {
      if (!subGrupos[t.grupo]) subGrupos[t.grupo] = [];
      subGrupos[t.grupo].push(t);
    } else {
      sinSubGrupo.push(t);
    }
  });
  const tieneSubGrupos = Object.keys(subGrupos).length > 0;

  return (
    <div>
      <div onClick={() => setOpen(v => !v)}
        style={{ padding: '6px 12px', fontSize: 10, fontWeight: 700, color, textTransform: 'uppercase', letterSpacing: 0.4, background: 'var(--surface2)', borderBottom: '0.5px solid var(--border2)', borderTop: '0.5px solid var(--border2)', cursor: 'pointer', userSelect: 'none', display: 'flex', alignItems: 'center', gap: 6 }}>
        <span>{g.tipo === 'exp' ? '📁' : '📋'} {g.label?.substring(0, 38)}</span>
        <span style={{ marginLeft: 'auto', fontSize: 10, color: 'var(--text4)', fontWeight: 400 }}>{g.tareas.length} {open ? '▼' : '▶'}</span>
      </div>
      {open && (
        tieneSubGrupos ? (
          <>
            {sinSubGrupo.map(t => renderTarea(t, true))}
            {Object.entries(subGrupos).map(([nombre, tareas]) => (
              <SubGrupoWidget key={nombre} nombre={nombre} tareas={tareas} renderTarea={renderTarea} />
            ))}
          </>
        ) : (
          g.tareas.map(t => renderTarea(t, true))
        )
      )}
    </div>
  );
}

function SubGrupoWidget({ nombre, tareas, renderTarea }) {
  const [open, setOpen] = useState(true);
  return (
    <div>
      <div onClick={() => setOpen(v => !v)}
        style={{ padding: '5px 16px', fontSize: 10, fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: 0.3, background: 'var(--surface)', borderBottom: '0.5px solid var(--border2)', borderTop: '0.5px solid var(--border2)', cursor: 'pointer', userSelect: 'none', display: 'flex', alignItems: 'center', gap: 6, borderLeft: '2px solid var(--border2)' }}>
        <span>▸ {nombre}</span>
        <span style={{ marginLeft: 'auto', fontSize: 10, color: 'var(--text4)', fontWeight: 400 }}>{tareas.length} {open ? '▼' : '▶'}</span>
      </div>
      {open && tareas.map(t => renderTarea(t, true))}
    </div>
  );
}

function clientesDeExpediente(exp) {
  if (!exp?.expedientes_clientes?.length) return '';
  return exp.expedientes_clientes.map(ec => `${ec.clientes?.apellido || ''} ${ec.clientes?.nombre || ''}`.trim()).filter(Boolean).join(', ');
}
function clientesDeTramite(tr) {
  if (!tr?.tramites_clientes?.length) return '';
  return tr.tramites_clientes.map(tc => `${tc.clientes?.apellido || ''} ${tc.clientes?.nombre || ''}`.trim()).filter(Boolean).join(', ');
}

// Props:
//   expedienteId, clienteId  — filtros opcionales
//   mode: 'page' | 'widget'
//   limit — max items en widget
//   onVerTarea(tarea), onCompletar(id), onNavigateExpediente(id), onNavigateTramite(id)
export default function TareasPanel({
  expedienteId = null,
  clienteId = null,
  mode = 'page',
  limit = 8,
  buscar = '',
  onVerTarea,
  onCompletar,
  onNavigateExpediente,
  onNavigateTramite,
  onTareasSaved,
  onCount,
}) {
  const [tareasConFecha, setTareasConFecha] = useState([]);
  const [tareasSinFecha, setTareasSinFecha] = useState([]);
  const [loading, setLoading] = useState(true);
  const [vencidasOpen, setVencidasOpen] = useState(true);
  const [seguimientoVinculo, setSeguimientoVinculo] = useState(null);

  const todayStr = localToday();

  function removerTarea(id) {
    setTareasConFecha(prev => prev.filter(t => t.id !== id));
    setTareasSinFecha(prev => prev.filter(t => t.id !== id));
  }

  async function completarTarea(id) {
    const { error } = await supabase.from('tareas').update({ completada: true, completada_en: new Date().toISOString() }).eq('id', id);
    if (error) { alert('Error al guardar: ' + error.message); return; }
    removerTarea(id);
    emitTareaChanged({ id, completada: true });
  }

  // Completa la tarea y abre el modal de nueva tarea ya vinculada al mismo
  // expediente/trámite, para agendar el siguiente paso sin tener que volver a buscarlo.
  async function completarYAgendar(t) {
    if (onCompletar) { await onCompletar(t.id); removerTarea(t.id); emitTareaChanged({ id: t.id, completada: true }); }
    else { await completarTarea(t.id); }
    setSeguimientoVinculo({ expediente_id: t.expediente_id || '', tramite_id: t.tramite_id || '' });
  }

  const fetchTareas = useCallback(async () => {
    setLoading(true);
    let q = supabase.from('tareas')
      .select('*, expedientes(caratula, numero, expedientes_clientes(clientes(nombre, apellido))), tramites(nombre, tramites_clientes(clientes(nombre, apellido)))')
      .eq('completada', false)
      .eq('descartada', false)
      .eq('eliminado', false)
      .order('fijada', { ascending: false })
      .order('fecha_vencimiento', { ascending: true, nullsFirst: false })
      .order('created_at', { ascending: true });

    if (expedienteId) q = q.eq('expediente_id', expedienteId);

    const { data } = await q;
    const todas = data || [];
    setTareasConFecha(todas.filter(t => t.fecha || t.fecha_vencimiento));
    setTareasSinFecha(todas.filter(t => !t.fecha && !t.fecha_vencimiento));
    setLoading(false);
  }, [expedienteId]);

  useEffect(() => { fetchTareas(); }, [fetchTareas]);

  // Permitir que el padre recargue
  useEffect(() => { if (onTareasSaved) fetchTareas(); }, [onTareasSaved, fetchTareas]);

  // Refrescar ante CUALQUIER escritura en tareas, sea cual sea el origen
  // (otro componente, un cascade de borrado de expediente/trámite, una RPC).
  // dataBus se dispara solo — no depende de que cada lugar recuerde emitir.
  useEffect(() => onDataChanged(() => fetchTareas(), 'tareas'), [fetchTareas]);

  const vencidas  = tareasConFecha.filter(t => t.fecha_vencimiento && t.fecha_vencimiento < todayStr);
  const proximas  = tareasConFecha.filter(t => !t.fecha_vencimiento || t.fecha_vencimiento >= todayStr);

  // Reportar al padre la cantidad de tareas pendientes (vencidas + sin fecha,
  // que es lo que efectivamente lista el modo widget) para que pueda mostrarla
  // junto al título "Tareas pendientes".
  useEffect(() => {
    onCount?.(vencidas.length + tareasSinFecha.length);
  }, [vencidas.length, tareasSinFecha.length, onCount]);

  const modalSeguimiento = (
    <TareaModal
      open={!!seguimientoVinculo}
      defaultExpedienteId={seguimientoVinculo?.expediente_id}
      defaultTramiteId={seguimientoVinculo?.tramite_id}
      onClose={() => setSeguimientoVinculo(null)}
      onSaved={() => { setSeguimientoVinculo(null); fetchTareas(); }}
    />
  );

  function renderTarea(t, compact = false) {
    const clientes = t.expedientes ? clientesDeExpediente(t.expedientes) : clientesDeTramite(t.tramites);
    const vencida  = t.fecha_vencimiento && t.fecha_vencimiento < todayStr;
    return (
      <div key={t.id} onClick={() => onVerTarea?.(t)}
        style={{ padding: compact ? '7px 12px' : '10px 14px', display: 'flex', gap: 10, alignItems: 'flex-start', cursor: 'pointer', borderBottom: '1px solid var(--border2)', background: 'var(--surface)' }}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, flexShrink: 0, marginTop: 3 }}>
          <input type="checkbox" style={{ cursor: 'pointer' }}
            onClick={e => e.stopPropagation()}
            onChange={async () => { if (onCompletar) { await onCompletar(t.id); removerTarea(t.id); emitTareaChanged({ id: t.id, completada: true }); } else { await completarTarea(t.id); } }} />
          <button type="button"
            title="Completar y agendar la próxima tarea para este expediente/trámite"
            onClick={e => { e.stopPropagation(); completarYAgendar(t); }}
            style={{ width: 16, height: 16, padding: 0, lineHeight: 1, fontSize: 11, fontWeight: 700, borderRadius: 4, border: '1px solid var(--border2)', background: 'var(--surface2)', color: 'var(--text4)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            +
          </button>
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: compact ? 12 : 13, fontWeight: t.fijada ? 600 : 500, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: compact ? 'nowrap' : 'normal', borderLeft: t.fijada ? `2px solid var(--blue)` : 'none', paddingLeft: t.fijada ? 6 : 0 }}>
            {t.titulo}
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginTop: 3, alignItems: 'center' }}>
            {t.prioridad === 'urgente' && <span style={{ fontSize: 10, background: '#fee2e2', color: 'var(--red)', padding: '1px 6px', borderRadius: 8 }}>Urgente</span>}
            {t.prioridad === 'muy importante' && <span style={{ fontSize: 10, background: '#fef3c7', color: '#b45309', padding: '1px 6px', borderRadius: 8 }}>Muy importante</span>}
            {compact && t.created_at && <span style={{ fontSize: 10, color: 'var(--text4)' }}>{diasDesdeCreacion(t.created_at)}</span>}
            {t.expedientes && (
              <span onClick={e => { e.stopPropagation(); onNavigateExpediente?.(t.expediente_id); }}
                style={{ fontSize: 11, color: 'var(--blue)', cursor: 'pointer' }}>
                {t.expedientes.caratula?.substring(0, 30)}
              </span>
            )}
            {t.tramites && (
              <span onClick={e => { e.stopPropagation(); onNavigateTramite?.(t.tramite_id); }}
                style={{ fontSize: 11, color: 'var(--blue)', cursor: 'pointer' }}>
                {t.tramites.nombre?.substring(0, 30)}
              </span>
            )}
            {clientes && <span style={{ fontSize: 11, color: 'var(--text3)' }}>{clientes.substring(0, 28)}</span>}
            {t.fecha_vencimiento && (
              <span style={{ fontSize: 10, color: vencida ? '#ef4444' : 'var(--text4)', fontWeight: vencida ? 600 : 400 }}>
                {vencida ? `Vencida ${formatDate(t.fecha_vencimiento)}` : `Vence ${formatDate(t.fecha_vencimiento)}`}
              </span>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ── WIDGET ──────────────────────────────────────────────────────────────────
  if (mode === 'widget') {
    if (loading) return <><div style={{ padding: '12px 0', color: 'var(--text4)', fontSize: 12 }}>Cargando…</div>{modalSeguimiento}</>;

    const norm = (s) => (s || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
    const q = norm(buscar);
    const matchTarea = (t) => {
      if (!q) return true;
      const clientes = t.expedientes ? clientesDeExpediente(t.expedientes) : clientesDeTramite(t.tramites);
      return norm(t.titulo).includes(q)
        || norm(t.expedientes?.caratula).includes(q)
        || norm(t.tramites?.nombre).includes(q)
        || norm(clientes).includes(q);
    };

    const todasPendientes = [...vencidas, ...tareasSinFecha].filter(matchTarea).sort((a, b) => {
      const aVenc = a.fecha_vencimiento && a.fecha_vencimiento < todayStr ? 0 : 1;
      const bVenc = b.fecha_vencimiento && b.fecha_vencimiento < todayStr ? 0 : 1;
      if (aVenc !== bVenc) return aVenc - bVenc;
      const aPeso = PRIORIDAD_PESO[a.prioridad] ?? 2;
      const bPeso = PRIORIDAD_PESO[b.prioridad] ?? 2;
      if (aPeso !== bPeso) return aPeso - bPeso;
      return (a.created_at || '').localeCompare(b.created_at || '');
    });
    if (todasPendientes.length === 0) return (
      <>
        <div style={{ padding: '12px 0', color: 'var(--text4)', fontSize: 12 }}>Sin tareas pendientes</div>
        {modalSeguimiento}
      </>
    );
    const grupos = {};
    const sinGrupo = [];
    todasPendientes.forEach(t => {
      const key = t.expediente_id ? `exp-${t.expediente_id}` : t.tramite_id ? `tra-${t.tramite_id}` : null;
      if (!key) { sinGrupo.push(t); return; }
      if (!grupos[key]) grupos[key] = { label: t.expedientes?.caratula || t.tramites?.nombre || '', tipo: t.expediente_id ? 'exp' : 'tra', tareas: [] };
      grupos[key].tareas.push(t);
    });
    return (
      <>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {sinGrupo.map(t => renderTarea(t, true))}
          {Object.entries(grupos).map(([key, g]) => (
            <GrupoWidget key={key} g={g} renderTarea={renderTarea} />
          ))}
        </div>
        {modalSeguimiento}
      </>
    );
  }

  // ── PAGE ─────────────────────────────────────────────────────────────────────
  const totalConFecha = tareasConFecha.length;

  return (
    <>
    <div>
      <div style={{ display: 'flex', gap: 6, marginBottom: 16, alignItems: 'center' }}>
        <span style={{ fontSize: 12, color: 'var(--text4)' }}>
          {totalConFecha} tarea{totalConFecha !== 1 ? 's' : ''} con fecha
        </span>
        {vencidas.length > 0 && (
          <span style={{ background: '#ef4444', color: '#fff', borderRadius: 10, fontSize: 10, padding: '1px 8px', fontWeight: 600 }}>
            {vencidas.length} vencida{vencidas.length > 1 ? 's' : ''}
          </span>
        )}
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 40, color: 'var(--text4)' }}>Cargando…</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {vencidas.length > 0 && (
            <div style={{ border: '1px solid #fecaca', borderRadius: 10, overflow: 'hidden' }}>
              <div onClick={() => setVencidasOpen(v => !v)}
                style={{ padding: '10px 16px', background: 'var(--surface2)', display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', userSelect: 'none', borderBottom: vencidasOpen ? '1px solid #fecaca' : 'none' }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text)', textTransform: 'uppercase', letterSpacing: 0.5 }}>Vencidas</span>
                <span style={{ fontSize: 11, color: '#ef4444', background: '#fee2e2', border: '1px solid #fecaca', borderRadius: 10, padding: '0 7px' }}>{vencidas.length}</span>
                <span style={{ fontSize: 11, color: 'var(--text4)', marginLeft: 'auto' }}>{vencidasOpen ? '▼' : '▶'}</span>
              </div>
              {vencidasOpen && <div>{vencidas.map(t => renderTarea(t))}</div>}
            </div>
          )}
          {proximas.length > 0 ? (() => {
            // Agrupar por fecha
            const grupos = {};
            const sinFecha = [];
            proximas.forEach(t => {
              const key = t.fecha_vencimiento || t.fecha;
              if (!key) { sinFecha.push(t); return; }
              if (!grupos[key]) grupos[key] = [];
              grupos[key].push(t);
            });
            const hoy = new Date(); hoy.setHours(0,0,0,0);
            const manana = new Date(hoy); manana.setDate(manana.getDate() + 1);
            const labelFecha = (key) => {
              const d = new Date(key + 'T12:00:00');
              const dStr = d.toDateString();
              if (dStr === hoy.toDateString()) return 'Hoy';
              if (dStr === manana.toDateString()) return 'Mañana';
              return d.toLocaleDateString('es-AR', { weekday: 'long', day: 'numeric', month: 'long' });
            };
            return (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {Object.entries(grupos).sort(([a],[b]) => a.localeCompare(b)).map(([key, tareas]) => {
                  const label = labelFecha(key);
                  const esFijo = label === 'Hoy' || label === 'Mañana';
                  return (
                    <GrupoFecha
                      key={key}
                      titulo={label}
                      subtitulo={esFijo ? new Date(key + 'T12:00:00').toLocaleDateString('es-AR', { day: 'numeric', month: 'short', year: 'numeric' }) : null}
                      count={tareas.length}
                    >
                      {tareas.map(t => renderTarea(t))}
                    </GrupoFecha>
                  );
                })}
                {sinFecha.length > 0 && (
                  <GrupoFecha titulo="Sin fecha asignada" count={sinFecha.length}>
                    {sinFecha.map(t => renderTarea(t))}
                  </GrupoFecha>
                )}
              </div>
            );
          })() : vencidas.length === 0 ? (
            <div className="empty-state"><div className="empty-state-icon">✅</div><div className="empty-state-title">Sin tareas con fecha</div></div>
          ) : null}
        </div>
      )}
    </div>
    {modalSeguimiento}
    </>
  );
}
